import { formatNumber } from '@rhino-project/core/i18n/format';
import { useTranslation, translate } from '@rhino-project/core/i18n';
import { Icon } from '@iconify/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { useModelIndex } from '@rhino-project/core/hooks';
import { FieldBooleanIcon } from '@rhino-project/ui-heroui';
import { FieldHidden } from '@rhino-project/ui-heroui';
import { FieldInput } from '@rhino-project/ui-heroui';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heroui/react';
import { usePlanningSeasons } from '../../hooks/ots';

const chipAccent =
  'inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-accent/15 text-accent border border-accent/30 cursor-pointer hover:bg-accent/25 min-h-[28px]';
const chipEmpty =
  'inline-flex items-center px-2.5 py-1 rounded-full text-xs border border-dashed border-separator text-muted cursor-pointer hover:bg-surface-secondary min-h-[28px]';

/* eslint-disable @typescript-eslint/no-explicit-any -- dynamic Rhino API records */
type RhinoAnyRecord = Record<string, any>;
type ResourcesWithResults = RhinoAnyRecord & {
  results?: RhinoAnyRecord[];
  total?: number;
};
/* eslint-enable @typescript-eslint/no-explicit-any */

type PositionChipFieldProps = {
  path: string;
  positions: ResourcesWithResults | undefined;
};

const PositionChipField = ({ path, positions }: PositionChipFieldProps) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const {
    field: { value, onChange }
  } = useController({ name: path });

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const currentPos = positions?.results?.find(
    (p) => `${p.id}` === `${value?.id || value}`
  );

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={currentPos ? chipAccent : chipEmpty}
      >
        {/* Abbreviated: this chip sits in a narrow column on a tablet, where
            "Right Defense" wraps to two lines and pushes the row taller. */}
        {currentPos?.abbreviation ||
          currentPos?.name ||
          t('frontend.messages.pos')}
      </button>
      {open && (
        <div
          ref={popoverRef}
          className="absolute top-full left-0 mt-1 bg-surface border border-separator rounded-3xl shadow-surface z-50 py-1 min-w-[100px]"
        >
          <button
            type="button"
            className="block w-full text-left px-3 py-1 text-sm text-muted hover:bg-surface-secondary"
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
          >
            --
          </button>
          {positions?.results?.map((pos) => (
            <button
              key={pos.id}
              type="button"
              className={`block w-full text-left px-3 py-1 text-sm hover:bg-surface-secondary ${
                `${pos.id}` === `${value?.id || value}`
                  ? 'bg-surface-secondary'
                  : ''
              }`}
              onClick={() => {
                onChange(pos.id);
                setOpen(false);
              }}
            >
              {pos.name || pos.abbreviation}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const handLabels = (): Record<string, string> => ({
  L: translate('frontend.messages.left'),
  R: translate('frontend.messages.right')
});

type HandChipFieldProps = {
  path: string;
};

const HandChipField = ({ path }: HandChipFieldProps) => {
  const { t } = useTranslation();

  const {
    field: { value, onChange }
  } = useController({ name: path });
  const options: (string | null)[] = [null, 'L', 'R'];

  const handleClick = () => {
    const currentIdx = options.indexOf(value);
    onChange(options[(currentIdx + 1) % options.length]);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={value ? chipAccent : chipEmpty}
    >
      {value ? handLabels()[value] : t('frontend.messages.hand_2')}
    </button>
  );
};

const chipWarning =
  'inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-warning/15 text-warning border border-warning/30 font-medium cursor-pointer hover:bg-warning/25 min-h-[28px]';

const ratingValues = Array.from({ length: 10 }, (_, i) =>
  ((i + 1) / 2).toFixed(1)
);

type RatingChipFieldProps = {
  path: string;
};

const RatingChipField = ({ path }: RatingChipFieldProps) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const {
    field: { value, onChange }
  } = useController({ name: path });

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const displayVal =
    value && parseFloat(value) > 0
      ? formatNumber(Number(value), {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        })
      : null;

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={displayVal ? chipWarning : chipEmpty}
      >
        {displayVal ? `★ ${displayVal}` : t('frontend.messages.rating_2')}
      </button>
      {open && (
        <div
          ref={popoverRef}
          className="absolute top-full left-0 mt-1 bg-surface border border-separator rounded-3xl shadow-surface z-50 py-1 min-w-[60px]"
        >
          <button
            type="button"
            className="block w-full text-left px-3 py-1 text-sm text-muted hover:bg-surface-secondary"
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
          >
            --
          </button>
          {ratingValues.map((val) => (
            <button
              key={val}
              type="button"
              className={`block w-full text-left px-3 py-1 text-sm hover:bg-surface-secondary ${
                val === `${value}` ? 'bg-surface-secondary' : ''
              }`}
              onClick={() => {
                onChange(val);
                setOpen(false);
              }}
            >
              {formatNumber(Number(val), {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
              })}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const FieldRating = RatingChipField;

const chipSuccess =
  'inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-success/15 text-success border border-success/30 cursor-pointer hover:bg-success/25 min-h-[28px]';

type GradYearChipFieldProps = {
  path: string;
};

const GradYearChipField = ({ path }: GradYearChipFieldProps) => {
  const { t } = useTranslation();

  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    field: { value, onChange }
  } = useController({ name: path });

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') setEditing(false);
          if (e.key === 'Escape') setEditing(false);
        }}
        placeholder={t('frontend.messages.year')}
        className="rounded-full px-2.5 py-1 text-xs bg-surface border border-separator focus:outline-none focus:ring-1 focus:ring-focus w-20 min-h-[28px]"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={value ? chipSuccess : chipEmpty}
    >
      {value
        ? t('frontend.messages.graduation_year', { year: value })
        : t('frontend.messages.grad_2')}
    </button>
  );
};

type RecruitForChipFieldProps = {
  path: string;
  seasons: ResourcesWithResults | undefined;
};

const RecruitForChipField = ({ path, seasons }: RecruitForChipFieldProps) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const {
    field: { value, onChange }
  } = useController({ name: path });

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const currentSeason = seasons?.results?.find((s) => `${s.id}` === `${value}`);

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={currentSeason ? chipSuccess : chipEmpty}
      >
        {currentSeason
          ? currentSeason.display_name
          : t('frontend.messages.recruit')}
      </button>
      {open && (
        <div
          ref={popoverRef}
          className="absolute top-full left-0 mt-1 bg-surface border border-separator rounded-3xl shadow-surface z-50 py-1 min-w-[120px]"
        >
          <button
            type="button"
            className="block w-full text-left px-3 py-1 text-sm text-muted hover:bg-surface-secondary"
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
          >
            --
          </button>
          {seasons?.results?.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`block w-full text-left px-3 py-1 text-sm hover:bg-surface-secondary ${
                `${s.id}` === `${value}` ? 'bg-surface-secondary' : ''
              }`}
              onClick={() => {
                onChange(s.id);
                setOpen(false);
              }}
            >
              {s.display_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const headerBase =
  'text-xs font-semibold uppercase tracking-wider text-muted h-6 flex items-center';

type PlayerHeaderLabelProps = {
  label: string;
  labelName: string;
  onClick: (labelName: string) => void;
};

const PlayerHeaderLabel = ({
  label,
  labelName,
  onClick
}: PlayerHeaderLabelProps) => {
  const handleClick = () => onClick(labelName);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`${headerBase} hover:text-foreground gap-1`}
    >
      {label} <Icon icon="bi:arrow-down-up" height={10} width={10} />
    </button>
  );
};

type PlayerHeaderProps = {
  onClick: (labelName: string) => void;
};

// Shared grid template for the player header + each player row. The "#"
// (jersey) column is a fixed 3rem so two-digit numbers aren't clipped; the
// remaining columns keep their 3:2:2:2:2 proportions. Header and rows MUST use
// the same template so columns stay aligned.
const playerGridCols =
  'grid grid-cols-[3rem_minmax(0,3fr)_minmax(0,2fr)_minmax(0,2fr)_minmax(0,2fr)_minmax(0,2fr)] gap-1';

export const PlayerHeader = (props: PlayerHeaderProps) => {
  const { t } = useTranslation();
  return (
    <div className={`${playerGridCols} px-1 mb-1`}>
      <PlayerHeaderLabel label="#" labelName="number" {...props} />
      <PlayerHeaderLabel
        label={t('frontend.messages.last_name')}
        labelName="last_name"
        {...props}
      />
      <PlayerHeaderLabel
        label={t('frontend.messages.position')}
        labelName="position"
        {...props}
      />
      <span className={headerBase}>{t('frontend.messages.shoots')}</span>
      <span className={headerBase}>{t('frontend.messages.rating')}</span>
      <div />
    </div>
  );
};

type PlayerCommittmentProps = {
  index: number;
  teamIndex: number;
};

const PlayerCommittment = ({ index, teamIndex }: PlayerCommittmentProps) => {
  const name = `report.teams.${teamIndex}.players.${index}.commitment`;
  const {
    field: { value }
  } = useController({ name });

  if (!value) return null;

  return (
    <Tooltip delay={300} closeDelay={0}>
      <TooltipTrigger>
        <span className="inline-flex items-center justify-center min-w-[24px] min-h-[24px] cursor-help">
          <Icon
            icon="bi:building"
            width="20"
            height="20"
            className="text-warning"
          />
        </span>
      </TooltipTrigger>
      <TooltipContent>{value}</TooltipContent>
    </Tooltip>
  );
};

type NoteRowProps = {
  path: string;
  onClose: () => void;
};

const NoteRow = ({ path, onClose }: NoteRowProps) => {
  const { t } = useTranslation();

  const {
    field: { value, onChange }
  } = useController({ name: path });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const closingRef = useRef(false);

  useEffect(() => {
    if (textareaRef.current) {
      const el = textareaRef.current;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, []);

  const handleBlur = useCallback(() => {
    // Delay to allow Done button click to register first
    setTimeout(() => {
      if (!closingRef.current) onClose();
    }, 150);
  }, [onClose]);

  const handleDone = useCallback(() => {
    closingRef.current = true;
    onClose();
  }, [onClose]);

  return (
    <div className="flex gap-2 items-start mt-1 mb-1">
      <textarea
        ref={textareaRef}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={handleBlur}
        placeholder={t('frontend.messages.add_a_note')}
        rows={2}
        className="flex-1 rounded-md border border-separator bg-surface px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-focus resize-none"
      />
      <button
        type="button"
        onMouseDown={handleDone}
        className="px-3 py-1.5 text-sm rounded-md bg-accent text-accent-foreground hover:bg-accent/90"
      >
        {t('frontend.messages.done')}
      </button>
    </div>
  );
};

type PlayerProps = {
  index: number;
  teamIndex: number;
};

const Player = ({ index, teamIndex }: PlayerProps) => {
  const [noteOpen, setNoteOpen] = useState(false);
  useFormContext();
  const { resources: positions } = useModelIndex('position', {
    networkOptions: {
      params: {
        limit: 1000
      }
    }
  });
  const { resources: planning_seasons } = usePlanningSeasons();

  const inputPath = useCallback(
    (path: string) => `report.teams.${teamIndex}.players.${index}.${path}`,
    [index, teamIndex]
  );

  const convert = useWatch({ name: inputPath('convert') });
  const noteValue = useWatch({ name: inputPath('note') });
  const hasNote = noteValue && noteValue.trim().length > 0;

  return (
    <div
      className={`mt-2 ${convert ? 'border-l-4 border-l-success pl-2' : ''}`}
    >
      <FieldHidden path={inputPath('id')} />
      <div className={`${playerGridCols} items-center`}>
        <div>
          <FieldInput path={inputPath('number')} />
        </div>
        <div>
          <FieldInput path={inputPath('last_name')} />
        </div>
        <div className="flex items-center">
          <PositionChipField
            path={inputPath('position')}
            positions={positions ?? undefined}
          />
        </div>
        <div className="flex items-center">
          <HandChipField path={inputPath('hand')} />
        </div>
        <div className="flex items-center">
          <RatingChipField path={inputPath('rating')} />
        </div>
        <div>
          <div className="flex items-center gap-1">
            <span className="inline-flex items-center justify-center min-w-[24px] min-h-[24px]">
              {/* icon/height/width passed through to FieldBooleanIconBase */}
              <FieldBooleanIcon
                path={inputPath('convert')}
                {...({
                  icon: 'bi:person-plus-fill',
                  height: 18,
                  width: 18
                } as Record<string, unknown>)}
              />
            </span>
            <span
              className={`inline-flex items-center justify-center min-w-[24px] min-h-[24px] cursor-pointer ${
                hasNote ? 'text-accent' : 'text-muted hover:text-foreground'
              }`}
              onClick={() => setNoteOpen(!noteOpen)}
            >
              <Icon icon="bi:pencil-square" height={18} width={18} />
            </span>
            <PlayerCommittment index={index} teamIndex={teamIndex} />
          </div>
        </div>
      </div>
      {noteOpen && (
        <NoteRow path={inputPath('note')} onClose={() => setNoteOpen(false)} />
      )}
      {convert && (
        <div className="flex items-center gap-1.5 mt-1 ml-1">
          <GradYearChipField path={inputPath('grad_year')} />
          {(planning_seasons?.total ?? 0) > 0 && (
            <RecruitForChipField
              path={inputPath('team_season')}
              seasons={planning_seasons ?? undefined}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Player;
